import React, { Suspense, lazy, Component } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

// ── Public / auth pages — direct imports (no lazy, no spinner on navigation)
import Home               from './pages/Home';
import WhySaferSavvy      from './pages/WhySaferSavvy';
import TransportDashboard from './pages/TransportDashboard';
import Login              from './pages/Login';
import Signup             from './pages/Signup';
import ForgotPassword     from './pages/ForgotPassword';
import PrivacyPolicy      from './pages/PrivacyPolicy';
import TermsOfService     from './pages/TermsOfService';

// ── Heavy dashboard pages — lazy loaded (only downloaded when needed)
const StudentDashboard = lazy(() => import('./pages/StudentDashboard'));
const AdminDashboard   = lazy(() => import('./pages/AdminDashboard'));
const DriverDashboard  = lazy(() => import('./pages/DriverDashboard'));

// ── Admin sub-pages — lazy loaded
const Dashboard           = lazy(() => import('./components/admin-dashboard/Dashboard'));
const FleetManagement     = lazy(() => import('./components/admin-dashboard/FleetManagement'));
const DriverManagement    = lazy(() => import('./components/admin-dashboard/DriverManagement'));
const DriverDetail        = lazy(() => import('./components/admin-dashboard/DriverDetail'));
const BulkImport          = lazy(() => import('./components/admin-dashboard/BulkImport'));
const BusDetail           = lazy(() => import('./components/admin-dashboard/BusDetail'));
const TripDetail          = lazy(() => import('./components/admin-dashboard/TripDetail'));
const StudentDetail       = lazy(() => import('./components/admin-dashboard/StudentDetail'));
const RouteManagement     = lazy(() => import('./components/admin-dashboard/RouteManagement'));
const BusManagement       = lazy(() => import('./components/admin-dashboard/BusManagement'));
const MapTracking         = lazy(() => import('./components/admin-dashboard/MapTracking'));
const Analytics           = lazy(() => import('./components/admin-dashboard/Analytics'));
const Complaints          = lazy(() => import('./components/admin-dashboard/Complaints'));
const ComplaintDetail     = lazy(() => import('./components/admin-dashboard/ComplaintDetail'));
const AdminProfile        = lazy(() => import('./components/admin-dashboard/AdminProfile'));
const SettingsPage        = lazy(() => import('./components/admin-dashboard/SettingsPage'));
const LookupsPage         = lazy(() => import('./components/admin-dashboard/lookups/LookupsPage'));
const AttendanceMarking   = lazy(() => import('./components/admin-dashboard/AttendanceMarking'));
const AttendanceAnalytics = lazy(() => import('./components/admin-dashboard/AttendanceAnalytics'));
const AttendanceReport    = lazy(() => import('./components/admin-dashboard/AttendanceReport'));
const DriverPerformance   = lazy(() => import('./components/admin-dashboard/DriverPerformance'));
const UserManagement      = lazy(() => import('./components/admin-dashboard/UserManagement'));
const StudentRegistrations = lazy(() => import('./components/admin-dashboard/StudentRegistrations'));
const StudentRegistrationsDetail = lazy(() => import('./components/admin-dashboard/StudentRegistrationsDetail'));
const StudentTripHistory  = lazy(() => import('./components/admin-dashboard/StudentTripHistory'));
const StudentTripHistoryDetail = lazy(() => import('./components/admin-dashboard/StudentTripHistoryDetail'));
const StudentCard         = lazy(() => import('./components/admin-dashboard/StudentCard'));
const StudentCardDetail   = lazy(() => import('./components/admin-dashboard/StudentCardDetail'));
const SecuritySettings        = lazy(() => import('./pages/SecuritySettings'));
const FeedbackPage            = lazy(() => import('./components/admin-dashboard/FeedbackPage'));
const BroadcastNotificationPage = lazy(() => import('./components/admin-dashboard/BroadcastNotificationPage'));

import ProtectedRoute from './components/common/ProtectedRoute';
import { AlertProvider } from './contexts/AlertContext';

/* ── Spinner shown only for heavy dashboard chunks ── */
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen bg-white dark:bg-ink-900">
    <div className="flex flex-col items-center gap-3">
      <div className="w-10 h-10 border-4 border-blue-100 border-t-[#1D3047] dark:border-ink-700 dark:border-t-accent-500 rounded-full animate-spin" />
      <p className="text-sm text-gray-400 dark:text-ink-300 font-medium">Loading…</p>
    </div>
  </div>
);

/* ── Error boundary — shows real error in dev, clean screen in prod ── */
interface EBState { hasError: boolean; error?: Error }
class ChunkErrorBoundary extends Component<{ children: React.ReactNode }, EBState> {
  state: EBState = { hasError: false };
  static getDerivedStateFromError(error: Error): EBState { return { hasError: true, error }; }
  render() {
    if (this.state.hasError) {
      const isDev = import.meta.env.DEV;
      return (
        <div className="flex flex-col items-center justify-center min-h-screen gap-4 px-6 text-center bg-white dark:bg-ink-900">
          <div className="w-14 h-14 rounded-2xl bg-red-50 dark:bg-red-500/15 flex items-center justify-center">
            <svg className="w-7 h-7 text-red-500 dark:text-red-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
            </svg>
          </div>
          <h2 className="text-lg font-bold text-gray-800 dark:text-ink-50">Something went wrong</h2>
          {isDev && this.state.error && (
            <pre className="text-left text-xs text-red-600 bg-red-50 border border-red-200 dark:text-red-300 dark:bg-red-500/10 dark:border-red-500/30 rounded-xl px-4 py-3 max-w-xl w-full overflow-auto">
              {this.state.error.message}
              {'\n\n'}
              {this.state.error.stack}
            </pre>
          )}
          {!isDev && <p className="text-sm text-gray-500 dark:text-ink-300 max-w-xs">The page failed to load. Check your connection and try again.</p>}
          <button onClick={() => window.location.reload()}
            className="bg-[#1D3047] hover:bg-[#2D5F9E] dark:bg-primary-600 dark:hover:bg-primary-500 text-white text-sm font-semibold px-6 py-2.5 rounded-xl transition-colors">
            Reload page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function App() {
  return (
    <>
      <AlertProvider>
      <div className="font-sans text-gray-800 dark:text-ink-100 overflow-x-hidden">
        <ChunkErrorBoundary>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* ── Public routes — instant, no spinner ── */}
              <Route path="/"                    element={<Home />} />
              <Route path="/why-safersavvy"      element={<WhySaferSavvy />} />
              <Route path="/transport-dashboard" element={<TransportDashboard />} />
              <Route path="/login"               element={<Login />} />
              <Route path="/signup"              element={<Signup />} />
              <Route path="/forgot-password"     element={<ForgotPassword />} />
              <Route path="/privacy-policy"      element={<PrivacyPolicy />} />
              <Route path="/terms-of-service"    element={<TermsOfService />} />

              {/* ── Student ── */}
              <Route path="/student-dashboard"
                element={<ProtectedRoute allowedRoles={["student"]}><StudentDashboard /></ProtectedRoute>}
              />

              {/* ── Admin ── */}
              <Route path="/admin-dashboard"
                element={<ProtectedRoute allowedRoles={["transportAdmin"]}><AdminDashboard /></ProtectedRoute>}
              >
                <Route index element={<Navigate to="/admin-dashboard/dashboard" replace />} />
                <Route path="dashboard"            element={<Dashboard />} />
                <Route path="fleet"                element={<FleetManagement />} />
                <Route path="bus"                  element={<BusManagement />} />
                <Route path="bus/add"              element={<BusManagement />} />
                <Route path="driver"               element={<DriverManagement />} />
                <Route path="driver/:id"           element={<DriverDetail />} />
                <Route path="bus-detail/:id"       element={<BusDetail />} />
                <Route path="student/:id"          element={<StudentDetail />} />
                <Route path="trip/:id"             element={<TripDetail />} />
                <Route path="bulk-import"          element={<BulkImport />} />
                <Route path="users"                element={<UserManagement />} />
                <Route path="routes"               element={<RouteManagement />} />
                <Route path="tracking"             element={<MapTracking />} />
                <Route path="registrations"        element={<StudentRegistrations />} />
                <Route path="registrations/:busId/:routeId/:tripType" element={<StudentRegistrationsDetail />} />
                <Route path="student-history"      element={<StudentTripHistory />} />
                <Route path="student-history/:id"  element={<StudentTripHistoryDetail />} />
                <Route path="student-card"         element={<StudentCard />} />
                <Route path="student-card/:id"     element={<StudentCardDetail />} />
                <Route path="attendance"           element={<AttendanceMarking />} />
                <Route path="attendance-analytics" element={<AttendanceAnalytics />} />
                <Route path="attendance-report"    element={<AttendanceReport />} />
                <Route path="driver-performance"   element={<DriverPerformance />} />
                <Route path="analytics"            element={<Analytics />} />
                <Route path="complaints"           element={<Complaints />} />
                <Route path="complaints/:id"       element={<ComplaintDetail />} />
                <Route path="feedback"             element={<FeedbackPage />} />
                <Route path="broadcast"           element={<BroadcastNotificationPage />} />
                <Route path="lookups"              element={<LookupsPage />} />
                <Route path="profile"              element={<AdminProfile />} />
                <Route path="settings"             element={<SettingsPage />} />
                <Route path="privacy-policy"       element={<PrivacyPolicy />} />
                <Route path="terms-of-service"     element={<TermsOfService />} />
                <Route path="security"             element={<SecuritySettings />} />
              </Route>

              {/* ── Driver ── */}
              <Route path="/driver-dashboard"
                element={<ProtectedRoute allowedRoles={["driver"]}><DriverDashboard /></ProtectedRoute>}
              />
            </Routes>
          </Suspense>
        </ChunkErrorBoundary>
      </div>
      </AlertProvider>
    </>
  );
}

export default App;
